import type { FC } from "react";
import React, { useEffect, useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { useTranslation } from "react-i18next";
import { useAskAgentMutation } from "@/redux/api/agentApi.ts";
import handleError from "../../redux/handleError.ts";
import styles from "./agentChatDrawer.module.scss";
import { useAgentChatUi } from "@/context/agent/useAgentChatUi.ts";
import { useAgentHistory } from "@/context/agent/useAgentHistory.ts";
import AgentHeader from "@/layout/agentChat/components/AgentHeader.tsx";
import AgentContent from "@/layout/agentChat/components/AgentContent.tsx";
import AgentFooter from "@/layout/agentChat/components/AgentFooter.tsx";

const MIN_QUERY_LENGTH = 2;

const AgentChatDrawer: FC = () => {
  const { t } = useTranslation("translation", { keyPrefix: "agentChat" });
  const { isOpen, close, key } = useAgentChatUi();
  const { history, appendEntry, clearHistory } = useAgentHistory();
  const [askAgent, { isLoading, error }] = useAskAgentMutation();


  const [inputValue, setInputValue] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmedInput = inputValue.trim();
  const canSend = trimmedInput.length >= MIN_QUERY_LENGTH && !isLoading;

  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);


  const handleClose = () => close();

  const handleClear = () => {
    clearHistory();
  };

  const sendQuery = (requestText: string, onError?: () => void) => {
    appendEntry(requestText);

    askAgent({ input: requestText })
      .unwrap()
      .then(response => {
        appendEntry(undefined, response);
      })
      .catch(() => {
        appendEntry(undefined, "Something went wrong. Please try again.");
        onError?.();
      });
  };

  const handleSend = () => {
    if (!canSend) return;

    const requestText = trimmedInput;
    setInputValue("");
    sendQuery(requestText, () => setInputValue(requestText));
  };

  const handleSelectPrompt = (text: string) => {
    sendQuery(text);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleSend();
  };

  const errorMessage = error != null ? handleError(error) ?? null : null;

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={isOpen}
      slotProps={{ paper: { className: styles.drawerPaper } }}
    >
      <div key={key} className={styles.container} role="dialog" aria-modal="false" aria-label={t("title")}>
        <AgentHeader showClearButton={history.length > 0} handleClose={handleClose} handleClear={handleClear} />
        <AgentContent
          isLoading={isLoading}
          error={error}
          errorMessage={errorMessage}
          history={history}
          listRef={listRef}
          onSelectPrompt={handleSelectPrompt}
        />
        <AgentFooter
          inputRef={inputRef}
          inputValue={inputValue}
          setInputValue={setInputValue}
          handleKeyDown={handleKeyDown}
          canSend={canSend}
          handleSend={handleSend}
        />
      </div>
    </Drawer>
  );
};

export default AgentChatDrawer;
